import { useState, useEffect, useCallback } from "react";
import { http } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Save, Plus, Trash2, GripVertical, ChevronDown, ChevronRight, Eye, EyeOff, ExternalLink, Lock, Monitor, Smartphone, RotateCcw, X } from "lucide-react";

export default function AdminNavManager() {
  const [nav, setNav] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editItem, setEditItem] = useState(null); // {groupIdx, itemIdx, childIdx?}

  const load = useCallback(async () => {
    try { const { data } = await http.get("/nav/admin"); setNav(data); }
    catch { toast.error("Failed to load"); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await http.put("/nav/admin", { groups: nav.groups });
      setNav(data);
      toast.success("Navigation saved — changes are live");
    } catch { toast.error("Failed to save"); }
    finally { setSaving(false); }
  };

  const reset = async () => {
    if (!window.confirm("Reset navigation to default? This cannot be undone.")) return;
    try {
      const { data } = await http.post("/nav/admin/reset");
      setNav(data);
      toast.success("Reset to default");
    } catch { toast.error("Failed"); }
  };

  const preview = () => window.open("/", "_blank");

  if (loading || !nav) return <div className="p-10 flex justify-center"><Loader2 className="animate-spin text-[#C9A055]" /></div>;

  const groups = nav.groups || [];

  const addGroup = () => {
    const g = { id: crypto.randomUUID().slice(0, 8), label: "NEW SECTION", sort_order: groups.length, visible: "both", items: [] };
    setNav(prev => ({ ...prev, groups: [...prev.groups, g] }));
  };

  const updateGroup = (gIdx, key, val) => {
    const gs = [...groups];
    gs[gIdx] = { ...gs[gIdx], [key]: val };
    setNav(prev => ({ ...prev, groups: gs }));
  };

  const removeGroup = (gIdx) => {
    if (!window.confirm(`Delete "${groups[gIdx].label}" and all its items?`)) return;
    setNav(prev => ({ ...prev, groups: prev.groups.filter((_, i) => i !== gIdx) }));
  };

  const moveGroup = (gIdx, dir) => {
    const gs = [...groups];
    const newIdx = gIdx + dir;
    if (newIdx < 0 || newIdx >= gs.length) return;
    [gs[gIdx], gs[newIdx]] = [gs[newIdx], gs[gIdx]];
    gs.forEach((g, i) => g.sort_order = i);
    setNav(prev => ({ ...prev, groups: gs }));
  };

  const addItem = (gIdx) => {
    const item = { id: crypto.randomUUID().slice(0, 8), label: "New Item", url: "/", visible: "both", login_required: false, new_tab: false, enabled: true, sort_order: groups[gIdx].items.length, children: [] };
    const gs = [...groups];
    gs[gIdx] = { ...gs[gIdx], items: [...gs[gIdx].items, item] };
    setNav(prev => ({ ...prev, groups: gs }));
  };

  const updateItem = (gIdx, iIdx, key, val) => {
    const gs = [...groups];
    const items = [...gs[gIdx].items];
    items[iIdx] = { ...items[iIdx], [key]: val };
    gs[gIdx] = { ...gs[gIdx], items };
    setNav(prev => ({ ...prev, groups: gs }));
  };

  const removeItem = (gIdx, iIdx) => {
    const gs = [...groups];
    gs[gIdx] = { ...gs[gIdx], items: gs[gIdx].items.filter((_, i) => i !== iIdx) };
    setNav(prev => ({ ...prev, groups: gs }));
  };

  const moveItem = (gIdx, iIdx, dir) => {
    const gs = [...groups];
    const items = [...gs[gIdx].items];
    const newIdx = iIdx + dir;
    if (newIdx < 0 || newIdx >= items.length) return;
    [items[iIdx], items[newIdx]] = [items[newIdx], items[iIdx]];
    items.forEach((it, i) => it.sort_order = i);
    gs[gIdx] = { ...gs[gIdx], items };
    setNav(prev => ({ ...prev, groups: gs }));
  };

  const addChild = (gIdx, iIdx) => {
    const child = { id: crypto.randomUUID().slice(0, 8), label: "Sub-item", url: "/", visible: "both", login_required: false, new_tab: false, enabled: true, sort_order: 0, children: [] };
    const gs = [...groups];
    const items = [...gs[gIdx].items];
    items[iIdx] = { ...items[iIdx], children: [...(items[iIdx].children || []), child] };
    gs[gIdx] = { ...gs[gIdx], items };
    setNav(prev => ({ ...prev, groups: gs }));
  };

  const updateChild = (gIdx, iIdx, cIdx, key, val) => {
    const gs = [...groups];
    const items = [...gs[gIdx].items];
    const children = [...(items[iIdx].children || [])];
    children[cIdx] = { ...children[cIdx], [key]: val };
    items[iIdx] = { ...items[iIdx], children };
    gs[gIdx] = { ...gs[gIdx], items };
    setNav(prev => ({ ...prev, groups: gs }));
  };

  const removeChild = (gIdx, iIdx, cIdx) => {
    const gs = [...groups];
    const items = [...gs[gIdx].items];
    items[iIdx] = { ...items[iIdx], children: items[iIdx].children.filter((_, i) => i !== cIdx) };
    gs[gIdx] = { ...gs[gIdx], items };
    setNav(prev => ({ ...prev, groups: gs }));
  };

  const VIS = { both: <Eye size={10} />, desktop: <Monitor size={10} />, mobile: <Smartphone size={10} /> };

  return (
    <div className="space-y-4" data-testid="admin-nav-manager">
      <div className="flex justify-between items-center gap-3">
        <h2 className="font-display text-3xl text-[#F4F1EA]">Navigation Manager</h2>
        <div className="flex gap-2">
          <button onClick={preview} className="flex items-center gap-1 border border-white/10 text-zinc-400 px-3 py-1.5 rounded-lg text-[10px] hover:border-zinc-600 transition-all" data-testid="nav-preview"><Eye size={12} /> Preview</button>
          <button onClick={reset} className="flex items-center gap-1 border border-white/10 text-zinc-400 px-3 py-1.5 rounded-lg text-[10px] hover:border-zinc-600 transition-all"><RotateCcw size={12} /> Reset</button>
          <button onClick={save} disabled={saving} data-testid="nav-save"
            className="flex items-center gap-2 bg-[#C9A055] text-[#1A1A1A] px-5 py-2 rounded-lg text-xs font-medium hover:bg-[#D4AF37] disabled:opacity-50">
            {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Publish
          </button>
        </div>
      </div>

      <p className="text-zinc-500 text-xs">Drag groups and items to reorder. Changes go live after clicking Publish.</p>

      {/* Groups */}
      <div className="space-y-4">
        {groups.map((group, gIdx) => (
          <div key={group.id} className="rounded-xl border border-white/[0.06] bg-white/[0.02] overflow-hidden" data-testid={`nav-group-${group.id}`}>
            {/* Group Header */}
            <div className="flex items-center gap-2 px-4 py-3 bg-white/[0.02] border-b border-white/[0.04]">
              <div className="flex flex-col gap-0.5">
                <button onClick={() => moveGroup(gIdx, -1)} disabled={gIdx === 0} className="text-zinc-600 hover:text-zinc-300 disabled:opacity-20"><ChevronDown size={10} className="rotate-180" /></button>
                <button onClick={() => moveGroup(gIdx, 1)} disabled={gIdx === groups.length - 1} className="text-zinc-600 hover:text-zinc-300 disabled:opacity-20"><ChevronDown size={10} /></button>
              </div>
              <input value={group.label} onChange={e => updateGroup(gIdx, "label", e.target.value)}
                className="flex-1 bg-transparent text-[#C9A055] font-ui text-xs tracking-[0.2em] focus:outline-none min-w-0" data-testid={`nav-group-label-${gIdx}`} />
              <select value={group.visible || "both"} onChange={e => updateGroup(gIdx, "visible", e.target.value)}
                className="bg-transparent border border-white/10 rounded px-1.5 py-0.5 text-[10px] text-zinc-400">
                <option value="both">All</option><option value="desktop">Desktop</option><option value="mobile">Mobile</option>
              </select>
              <button onClick={() => addItem(gIdx)} className="text-[#C9A055] hover:text-[#D4AF37] p-1" title="Add item"><Plus size={14} /></button>
              <button onClick={() => removeGroup(gIdx)} className="text-zinc-600 hover:text-red-400 p-1"><Trash2 size={12} /></button>
            </div>

            {/* Items */}
            <div className="divide-y divide-white/[0.03]">
              {group.items.map((item, iIdx) => (
                <div key={item.id}>
                  <div className="flex items-center gap-2 px-4 py-2.5 hover:bg-white/[0.02]">
                    <div className="flex flex-col gap-0.5 shrink-0">
                      <button onClick={() => moveItem(gIdx, iIdx, -1)} disabled={iIdx === 0} className="text-zinc-700 hover:text-zinc-400 disabled:opacity-20"><ChevronDown size={9} className="rotate-180" /></button>
                      <button onClick={() => moveItem(gIdx, iIdx, 1)} disabled={iIdx === group.items.length - 1} className="text-zinc-700 hover:text-zinc-400 disabled:opacity-20"><ChevronDown size={9} /></button>
                    </div>
                    <div className="flex-1 grid grid-cols-[1fr_1fr] gap-2 min-w-0">
                      <input value={item.label} onChange={e => updateItem(gIdx, iIdx, "label", e.target.value)}
                        className="bg-transparent text-[#F4F1EA] text-xs focus:outline-none focus:border-b focus:border-[#C9A055] min-w-0 truncate" />
                      <input value={item.url} onChange={e => updateItem(gIdx, iIdx, "url", e.target.value)}
                        className="bg-transparent text-zinc-500 text-[10px] focus:outline-none focus:border-b focus:border-[#C9A055] min-w-0 truncate" placeholder="/path" />
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      <button onClick={() => updateItem(gIdx, iIdx, "enabled", !item.enabled)}
                        className={`p-1 ${item.enabled ? "text-green-400" : "text-zinc-700"}`} title={item.enabled ? "Visible" : "Hidden"}>
                        {item.enabled ? <Eye size={11} /> : <EyeOff size={11} />}
                      </button>
                      {item.login_required && <Lock size={10} className="text-amber-500" title="Login required" />}
                      {item.new_tab && <ExternalLink size={10} className="text-zinc-500" title="Opens in new tab" />}
                      <select value={item.visible || "both"} onChange={e => updateItem(gIdx, iIdx, "visible", e.target.value)}
                        className="bg-transparent border-none text-[9px] text-zinc-500 w-12 p-0">
                        <option value="both">All</option><option value="desktop">DT</option><option value="mobile">MB</option>
                      </select>
                      <button onClick={() => updateItem(gIdx, iIdx, "login_required", !item.login_required)}
                        className={`p-0.5 text-[9px] ${item.login_required ? "text-amber-400" : "text-zinc-700"}`} title="Toggle login required">
                        <Lock size={10} />
                      </button>
                      <button onClick={() => updateItem(gIdx, iIdx, "new_tab", !item.new_tab)}
                        className={`p-0.5 ${item.new_tab ? "text-blue-400" : "text-zinc-700"}`} title="Toggle new tab">
                        <ExternalLink size={10} />
                      </button>
                      <button onClick={() => addChild(gIdx, iIdx)} className="text-zinc-600 hover:text-[#C9A055] p-0.5" title="Add sub-item"><Plus size={11} /></button>
                      <button onClick={() => removeItem(gIdx, iIdx)} className="text-zinc-700 hover:text-red-400 p-0.5"><Trash2 size={11} /></button>
                    </div>
                  </div>

                  {/* Children */}
                  {(item.children || []).length > 0 && (
                    <div className="pl-10 bg-white/[0.01]">
                      {item.children.map((child, cIdx) => (
                        <div key={child.id} className="flex items-center gap-2 px-4 py-2 border-t border-white/[0.02]">
                          <ChevronRight size={10} className="text-zinc-700 shrink-0" />
                          <div className="flex-1 grid grid-cols-[1fr_1fr] gap-2 min-w-0">
                            <input value={child.label} onChange={e => updateChild(gIdx, iIdx, cIdx, "label", e.target.value)}
                              className="bg-transparent text-zinc-300 text-[11px] focus:outline-none min-w-0 truncate" />
                            <input value={child.url} onChange={e => updateChild(gIdx, iIdx, cIdx, "url", e.target.value)}
                              className="bg-transparent text-zinc-600 text-[10px] focus:outline-none min-w-0 truncate" />
                          </div>
                          <button onClick={() => updateChild(gIdx, iIdx, cIdx, "enabled", !child.enabled)}
                            className={`p-0.5 ${child.enabled ? "text-green-400" : "text-zinc-700"}`}>
                            {child.enabled ? <Eye size={10} /> : <EyeOff size={10} />}
                          </button>
                          <button onClick={() => removeChild(gIdx, iIdx, cIdx)} className="text-zinc-700 hover:text-red-400 p-0.5"><Trash2 size={10} /></button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      <button onClick={addGroup} data-testid="nav-add-group"
        className="w-full border border-dashed border-white/10 rounded-xl py-4 text-zinc-500 text-xs font-ui tracking-wider hover:border-[#C9A055]/30 hover:text-[#C9A055] transition-all">
        + Add Section
      </button>
    </div>
  );
}
